import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoginForm } from '@flexibilitypay/ui';
import { AuthApiError, authApi, useAuthStore } from '@flexibilitypay/shared';

import { ROUTES } from '../routes/routes-config';
import { ChangePasswordDialog } from './auth/change-password-dialog';

export function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['auth']);
  const [searchParams] = useSearchParams();
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const setUser = useAuthStore((s) => s.setUser);
  const setSession = useAuthStore((s) => s.setSession);

  const [error, setError] = useState<string | null>(() =>
    searchParams.get('reason') === 'session-expired'
      ? t('auth:login.errors.sessionExpired')
      : null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [changePasswordState, setChangePasswordState] = useState<
    | {
        open: true;
        username: string;
        currentPassword: string;
      }
    | { open: false }
  >({ open: false });

  if (isLoggedIn) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  async function performLogin(username: string, password: string): Promise<boolean> {
    try {
      const session = await authApi.login({ username, password });
      setSession({
        accessToken: session.accessToken,
        refreshToken: session.refreshToken,
        expiresAt: session.expiresAt,
        refreshExpiresAt: session.refreshExpiresAt,
        role: session.role,
        tenantId: session.tenantId,
      });
      setUser({ id: username, email: username });
      navigate(ROUTES.DASHBOARD, { replace: true });
      return true;
    } catch (err) {
      const appError = err instanceof AuthApiError ? err.appError : undefined;
      if (appError?.httpStatus === 403 && appError.code === 'AUTH_004') {
        setChangePasswordState({ open: true, username, currentPassword: password });
        setError(null);
        return false;
      }
      setError(mapLoginErrorToMessage(t, appError));
      return false;
    }
  }

  async function handleSubmit({ username, password }: { username: string; password: string }) {
    setError(null);
    setSubmitting(true);
    try {
      await performLogin(username.trim(), password.trim());
    } finally {
      setSubmitting(false);
    }
  }

  async function handleChangePasswordSuccess({
    username,
    newPassword,
  }: {
    username: string;
    newPassword: string;
  }) {
    setChangePasswordState({ open: false });
    setSubmitting(true);
    try {
      await performLogin(username, newPassword);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <LoginForm
        appName={t('auth:login.appName')}
        onSubmit={handleSubmit}
        error={error}
        isLoading={submitting}
      />
      {changePasswordState.open && (
        <ChangePasswordDialog
          open
          username={changePasswordState.username}
          initialCurrentPassword={changePasswordState.currentPassword}
          onClose={() => setChangePasswordState({ open: false })}
          onSuccess={handleChangePasswordSuccess}
        />
      )}
    </>
  );
}

function mapLoginErrorToMessage(
  t: (key: string) => string,
  appError: { httpStatus?: number; code?: string } | undefined,
): string {
  const status = appError?.httpStatus;
  const code = appError?.code;
  if (status === 401 || code === 'AUTH_001' || code === 'AUTH_INVALID_CREDENTIALS') {
    return t('auth:login.errors.invalidCredentials');
  }
  if (status === 403 && code === 'AUTH_003') {
    return t('auth:login.errors.accountDisabled');
  }
  if (status === 429) {
    return t('auth:login.errors.tooManyAttempts');
  }
  return t('auth:login.errors.generic');
}
